import { FC, ReactNode } from 'react'
import classNames from 'classnames'
import dayjs, { Dayjs } from 'dayjs'
import { DatePicker, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import Preloader from '@UI/Preloader'
import type { ContractConstructorGroup } from '@pages/Admin/PlatformSettings2/SettingUpContracts/api/contractTemplateConstructor.types'
import {
  getGroupSectionTitle,
  isDateConstructorField,
  PARENT_GROUP_CODES,
  PRODUCT_GROUP_CODES,
} from '../utils/contractTemplateConstructor.utils'
import classes from '../AddTemplateContractModal.module.css'

const LoadingIcon = LoadingOutlined as unknown as FC<{ spin?: boolean }>

const CALCULATOR_FIELD_HINT =
  'Это вычисляемое поле: результат строится по формуле из других переменных. Если изменить исходные значения, итог пересчитается автоматически.'

type ConstructorGroupFieldsProps = {
  group: ContractConstructorGroup
  values: Record<string, string>
  invalidFieldCodes: Set<string>
  recalculatingCodes?: Set<string>
  onChange: (code: string, value: string) => void
  groupSelector?: ReactNode
  isLoading?: boolean
}

const ConstructorGroupFields: FC<ConstructorGroupFieldsProps> = ({
  group,
  values,
  invalidFieldCodes,
  recalculatingCodes,
  onChange,
  groupSelector = null,
  isLoading = false,
}) => {
  const isParentGroup = PARENT_GROUP_CODES.has(group.code)
  const isProductGroup = PRODUCT_GROUP_CODES.has(group.code)

  return (
    <section className={classes.groupSection}>
      <div className={classes.groupHeader}>
        <h3 className={classes.groupTitle}>{getGroupSectionTitle(group)}</h3>
      </div>
      {group.hint ? <p className={classes.groupHint}>{group.hint}</p> : null}

      {groupSelector ? (
        <div
          className={classNames(
            classes.groupSelector,
            isParentGroup && classes.groupSelectorParent,
            isProductGroup && classes.groupSelectorProduct,
          )}
        >
          {groupSelector}
        </div>
      ) : null}

      <div className={classes.groupFields}>
        {isLoading ? (
          <div className={classes.groupLoader}>
            <Preloader />
          </div>
        ) : (
          group.items.map((item) => {
            const fieldValue = values[item.code] ?? ''
            const isInvalid = invalidFieldCodes.has(item.code)
            const isCalculator = item.type === 'calculator'
            const isNumericField =
              item.type === 'number' || item.type === 'calculator'
            const isRecalculating = Boolean(
              isCalculator && recalculatingCodes?.has(item.code),
            )

            if (isDateConstructorField(item.code)) {
              const parsedDate = fieldValue ? dayjs(fieldValue) : null

              return (
                <FieldWrapper
                  key={item.code}
                  label={item.title}
                  isInvalid={isInvalid}
                >
                  <DatePicker
                    className={classNames(
                      classes.datePicker,
                      isInvalid && classes.datePickerInvalid,
                    )}
                    value={parsedDate?.isValid() ? parsedDate : null}
                    status={isInvalid ? 'error' : undefined}
                    format="YYYY-MM-DD"
                    placeholder=""
                    disabled={!item.editable}
                    onChange={(date: Dayjs | null) => {
                      onChange(item.code, date ? date.format('YYYY-MM-DD') : '')
                    }}
                  />
                </FieldWrapper>
              )
            }

            return (
              <div
                key={item.code}
                className={classNames(
                  isInvalid && classes.fieldInvalid,
                  isCalculator && classes.calculatorField,
                )}
              >
                <TextField
                  label={item.title}
                  value={fieldValue}
                  onChange={(value) => onChange(item.code, value)}
                  placeholder=""
                  type={isNumericField ? 'number' : undefined}
                  inputMode={isNumericField ? 'decimal' : undefined}
                  className={classNames(
                    classes.input,
                    isRecalculating && classes.calculatorFieldRecalculating,
                  )}
                  disabled={!item.editable || isRecalculating}
                  isInvalid={isInvalid}
                  tooltip={isCalculator ? CALCULATOR_FIELD_HINT : undefined}
                  extraContent={
                    isCalculator ? (
                      <span className={classes.calculatorFieldMeta}>
                        <span className={classes.calculatorFieldBadge}>
                          По формуле
                        </span>
                        {isRecalculating ? (
                          <span className={classes.calculatorFieldLoading}>
                            <Spin
                              indicator={<LoadingIcon spin />}
                              size="small"
                            />
                            <span>Пересчёт…</span>
                          </span>
                        ) : null}
                      </span>
                    ) : undefined
                  }
                />
              </div>
            )
          })
        )}
      </div>
    </section>
  )
}

export default ConstructorGroupFields
